import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '../../common/Modal';
import Button from '../../common/Button';
import { Input } from '../../common/FormField';
import { useCannedJobs } from '../hooks/useCannedJobs';
import { cannedJobSummary } from '../../../lib/cannedJob';

// A nested modal inside the estimate form. It loads the shop's canned jobs when
// it mounts (the estimate renders it only while open), adds as many as the
// mechanic taps, and closes on Done.
//
// preventBodyScroll is off on purpose: the estimate modal underneath already
// holds the page scroll lock, and this modal's cleanup would otherwise release
// it while the estimate is still open.
const CannedJobPicker = ({ onAdd, onClose }) => {
  const { cannedJobs, loading, error, reload } = useCannedJobs();
  const [query, setQuery] = useState('');
  const [addedName, setAddedName] = useState('');

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? cannedJobs.filter((job) => job.name.toLowerCase().includes(needle))
    : cannedJobs;

  const handleAdd = (job) => {
    onAdd(job);
    setAddedName(job.name);
  };

  return (
    <Modal isOpen onClose={onClose} title="Add canned job" size="md" preventBodyScroll={false}>
      <div className="space-y-3">
        {error && (
          <div className="space-y-2">
            <p role="alert" className="text-small text-danger">{error}</p>
            <Button variant="secondary" size="sm" onClick={reload}>Retry</Button>
          </div>
        )}
        {loading && <p className="text-small text-ink-muted">Loading…</p>}

        {!loading && !error && cannedJobs.length === 0 && (
          <p className="text-body text-ink-muted">
            No canned jobs yet — set them up in Settings → Canned jobs.
          </p>
        )}

        {cannedJobs.length > 0 && (
          <Input
            type="search"
            aria-label="Search canned jobs"
            placeholder="Search canned jobs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}

        {cannedJobs.length > 0 && visible.length === 0 && (
          <p className="text-body text-ink-muted">No canned jobs match "{query.trim()}".</p>
        )}

        {visible.length > 0 && (
          <ul className="divide-y divide-border-default max-h-96 overflow-y-auto">
            {visible.map((job) => (
              <li key={job.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-body-strong text-ink truncate">{job.name}</p>
                  <p className="text-small text-ink-muted">{cannedJobSummary(job)}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Plus}
                  aria-label={`Add ${job.name}`}
                  onClick={() => handleAdd(job)}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}

        <p role="status" className="text-small text-success min-h-[1.25rem]">
          {addedName ? `Added ${addedName}` : ''}
        </p>

        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
};

export default CannedJobPicker;
